import React, { useMemo } from "react";
import {
  Box,
  Container,
  Flex,
  HStack,
  VStack,
  Text,
  Image,
  Button,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Divider,
  useDisclosure,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HamburgerIcon } from "@chakra-ui/icons";

/* =========================
   Suffix-based language routing
========================= */
const toFrenchPath = (path) => {
  if (!path || path === "/") return "/fr";
  if (path === "/fr") return "/fr";
  if (path.endsWith("-fr")) return path;
  return `${path}-fr`;
};

const toEnglishPath = (path) => {
  if (!path || path === "/") return "/";
  if (path === "/fr") return "/";
  if (path.endsWith("-fr")) return path.replace(/-fr$/, "");
  return path;
};

const isFrenchPath = (path) => path === "/fr" || path.endsWith("-fr");

/* =========================
   Language Toggle
========================= */
function LanguageToggle({ compact = false, onDone }) {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  const current = pathname || "/";
  const currentlyFR = isFrenchPath(current);

  const go = (nextLang) => {
    const nextPath = nextLang === "fr" ? toFrenchPath(current) : toEnglishPath(current);
    navigate(`${nextPath}${search || ""}${hash || ""}`);
    onDone?.();
  };

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const muted = useColorModeValue("#475569", "#AAB3C2");
  const accent = "#06B6D4";

  return (
    <HStack
      spacing={1}
      border="1px solid"
      borderColor={border}
      borderRadius="999px"
      p="2px"
      bg={useColorModeValue("rgba(255,255,255,0.65)", "rgba(255,255,255,0.04)")}
    >
      <Button
        size={compact ? "xs" : "sm"}
        borderRadius="999px"
        variant={!currentlyFR ? "solid" : "ghost"}
        bg={!currentlyFR ? accent : "transparent"}
        color={!currentlyFR ? "#001018" : muted}
        fontWeight="900"
        onClick={() => go("en")}
        _hover={{
          filter: !currentlyFR ? "brightness(0.95)" : "none",
          bg: !currentlyFR ? accent : "transparent",
        }}
      >
        EN
      </Button>

      <Button
        size={compact ? "xs" : "sm"}
        borderRadius="999px"
        variant={currentlyFR ? "solid" : "ghost"}
        bg={currentlyFR ? accent : "transparent"}
        color={currentlyFR ? "#001018" : muted}
        fontWeight="900"
        onClick={() => go("fr")}
        _hover={{
          filter: currentlyFR ? "brightness(0.95)" : "none",
          bg: currentlyFR ? accent : "transparent",
        }}
      >
        FR
      </Button>
    </HStack>
  );
}

export default function NavbarFR() {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Always French component, but still detect for active highlight safely
  const localPath = toEnglishPath(location.pathname) || "/";

  // Labels (FR)
  const t = useMemo(
    () => ({
      project: "TRAVERSAL",
      tagline: "Formation et recherche sur les véhicules autonomes",
      home: "Accueil",
      team: "L'Équipe",
      program: "Programme",
      seminars: "Séminaires",
      publications: "Publications",
      news: "Nouvelles",
      contact: "Contactez-Nous",
      menu: "Menu",
      officialTitle:
        "FORMATION ET RECHERCHE SUR LES VÉHICULES AUTONOMES POUR DES SERVICES FIABLES DANS LES AIRS ET SUR TERRE (TRAVERSAL)",
    }),
    []
  );

  // French links (explicit)
  const links = useMemo(
    () => [
      { to: "/fr", key: "/", label: t.home },
      { to: "/about-us-fr", key: "/about-us", label: t.team },
      { to: "/program-fr", key: "/program", label: t.program },
      { to: "/seminars-fr", key: "/seminars", label: t.seminars },
      { to: "/publications-fr", key: "/publications", label: t.publications },
      { to: "/news-fr", key: "/news", label: t.news },
    ],
    [t]
  );

  const contactTo = "/contact-us-fr";

  // Palette
  const pageBg = useColorModeValue("white", "#0B1220");
  const headerBg = useColorModeValue("#e2e8f0c4", "#0B1220");
  const stripBg = useColorModeValue("#f1f5f9", "#0F172A");

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const text = useColorModeValue("#38549a", "#E5E7EB");
  const muted = useColorModeValue("#475569", "#AAB3C2");
  const accent = "#06B6D4";
  const accent2 = "#22D3EE";

  const NavItem = ({ to, label, isActive, onClick }) => (
    <Button
      as={Link}
      to={to}
      onClick={onClick}
      variant="ghost"
      size="sm"
      px={3}
      py={2}
      borderRadius="999px"
      fontWeight="800"
      color={isActive ? text : muted}
      _hover={{
        bg: useColorModeValue("rgba(15,23,42,0.04)", "rgba(148,163,184,0.10)"),
        color: text,
      }}
      position="relative"
    >
      {label}
      <Box
        position="absolute"
        left="18%"
        right="18%"
        bottom="-8px"
        height="2px"
        borderRadius="999px"
        bg={accent}
        opacity={isActive ? 1 : 0}
        transform={isActive ? "scaleX(1)" : "scaleX(0.65)"}
        transition="all 180ms ease"
      />
    </Button>
  );

  return (
    <>
      <Box
        position="sticky"
        top={0}
        zIndex={1000}
        bg={headerBg}
        backdropFilter="blur(14px)"
        borderBottom="1px solid"
        borderColor={border}
      >
        <Box h="3px" bg={`linear-gradient(90deg, ${accent} 0%, ${accent2} 40%, rgba(34,211,238,0) 100%)`} />

        <Container maxW="1200px" py={{ base: 2.5, md: 3.5 }}>
          <Flex align="center" justify="space-between" gap={3}>
            <HStack spacing={{ base: 3, md: 4 }} align="center" minW={0} flex="1">
              <Box
                border="1px solid"
                borderColor={useColorModeValue("rgba(2,6,23,0.08)", "rgba(148,163,184,0.18)")}
                borderRadius="16px"
                bg={useColorModeValue("rgba(255,255,255,0.60)", "rgba(255,255,255,0.04)")}
                px={{ base: 2, md: 2.5 }}
                py={{ base: 1.2, md: 1.6 }}
                flexShrink={0}
              >
                <Image
                  src="traversal_logo.png"
                  alt="TRAVERSAL"
                  maxW={{ base: "100px", md: "120px", lg: "120px" }}
                  w="100%"
                  h="auto"
                  objectFit="contain"
                  objectPosition="left center"
                  draggable={false}
                />
              </Box>

              <Box minW={0} lineHeight={1.15}>
                <Text
                  fontSize={{ base: "sm", md: "sm" }}
                  fontWeight="900"
                  color={text}
                  letterSpacing="0.02em"
                  whiteSpace={{ base: "normal", md: "nowrap" }}
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {t.project}
                </Text>
                <Text
                  fontSize={{ base: "xs", md: "sm" }}
                  color={muted}
                  fontWeight="700"
                  whiteSpace={{ base: "normal", md: "nowrap" }}
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {t.tagline}
                </Text>
              </Box>
            </HStack>

            <HStack spacing={2} flexShrink={0}>
              <Box display={{ base: "none", md: "block" }}>
                <LanguageToggle />
              </Box>

              <Button
                as={Link}
                to={contactTo}
                size="sm"
                display={{ base: "none", md: "inline-flex" }}
                bg={accent}
                color="#001018"
                fontWeight="900"
                borderRadius="999px"
                _hover={{ filter: "brightness(0.95)" }}
              >
                {t.contact}
              </Button>

              <IconButton
                aria-label="Open menu"
                icon={<HamburgerIcon />}
                variant="ghost"
                display={{ base: "inline-flex", md: "none" }}
                onClick={onOpen}
              />
            </HStack>
          </Flex>
        </Container>

        <Box bg={useColorModeValue("rgba(15,23,42,0.02)", "rgba(148,163,184,0.06)")}>
          <Container maxW="1200px" py={2}>
            <Flex align="center" justify="space-between">
              <HStack spacing={1} display={{ base: "none", md: "flex" }}>
                {links.map((l) => (
                  <NavItem key={l.key} to={l.to} label={l.label} isActive={localPath === l.key} />
                ))}
              </HStack>

              <Text display={{ base: "none", lg: "block" }} fontSize="sm" color={muted} fontWeight="700" letterSpacing="0.01em">
                {"Recherche ouverte • Publications • Séminaires"}
              </Text>
            </Flex>
          </Container>
        </Box>

        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
          <DrawerOverlay />
          <DrawerContent bg={pageBg}>
            <DrawerCloseButton />
            <DrawerHeader>
              <VStack align="start" spacing={2}>
                <Text fontWeight="900" color={text}>
                  {t.menu}
                </Text>
                <LanguageToggle compact onDone={onClose} />
              </VStack>
            </DrawerHeader>

            <DrawerBody>
              <VStack align="stretch" spacing={1}>
                {links.map((l) => (
                  <Button
                    key={l.key}
                    as={Link}
                    to={l.to}
                    onClick={onClose}
                    justifyContent="flex-start"
                    variant="ghost"
                    fontWeight="800"
                    color={localPath === l.key ? text : muted}
                    _hover={{
                      bg: useColorModeValue("rgba(15,23,42,0.04)", "rgba(148,163,184,0.10)"),
                    }}
                  >
                    {l.label}
                  </Button>
                ))}

                <Divider my={3} borderColor={border} />

                <Button
                  as={Link}
                  to={contactTo}
                  onClick={onClose}
                  bg={accent}
                  color="#001018"
                  fontWeight="900"
                  borderRadius="999px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  {t.contact}
                </Button>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Box bg={stripBg} borderBottom="1px solid" borderColor={border}>
        <Container maxW="1200px" py={{ base: 2, md: 3 }}>
          {!isMobile ? (
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="900" color={text} textAlign="center" letterSpacing="0.03em">
              {t.officialTitle}
            </Text>
          ) : (
            <Box overflow="hidden" w="100%">
              <Box
                display="inline-flex"
                alignItems="center"
                whiteSpace="nowrap"
                sx={{
                  "@keyframes traversalMarquee": {
                    "0%": { transform: "translateX(0)" },
                    "100%": { transform: "translateX(-50%)" },
                  },
                  animation: "traversalMarquee 16s linear infinite",
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }}
              >
                <Text as="span" fontSize="xs" fontWeight="900" color={text} letterSpacing="0.03em">
                  {t.officialTitle}
                </Text>
                <Box as="span" px={6} />
                <Text as="span" fontSize="xs" fontWeight="900" color={text} letterSpacing="0.03em">
                  {t.officialTitle}
                </Text>
              </Box>
            </Box>
          )}
        </Container>
      </Box>


      </Box>

      
    </>
  );
}
